// @flow
import { useCallback } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { getCart, updateCart } from '@gandi/cart';

import { localeSelector } from '@gandi/translate';

import { navigateTo } from 'src/helpers/navigation';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectShopURL } from 'src/modules/config';

import type { OrganizationFormData } from '../types';

import { useContinueClick } from './useContinueClick';

export function useFirstBuyNavigation({
  chooseProductOwner,
  type,
}: {
  chooseProductOwner: boolean,
  type: OrganizationFormData['type'],
}): (id: string) => Promise<void> {
  const navigate = useNavigate();
  const params = useParams<{ cartId: string }>();
  const dispatch = useDispatch();
  const locale = useAppSelector(localeSelector);
  const shop = useAppSelector(selectShopURL);
  const handleContinueClick = useContinueClick();

  return useCallback(
    async (orgId: string) => {
      // chooseProductOwner tell that the user want different product owner than the current one
      // but the company type does not have this choice
      // so company type should always go to the account-choice page, and not continue to the next step
      if (!chooseProductOwner && type !== 'company') {
        await handleContinueClick({ orgId, reload: type !== 'individual' });
        return;
      }

      try {
        await dispatch(
          updateCart(
            params.cartId,
            // NOTE: no has_orga, because user is redirected to account-choice
            { owner_uuid: orgId },
            { headers: { 'X-Dry-Run': '0' } },
          ),
        );
        await dispatch(getCart());

        const relativeUrl = `/cart/${params.cartId}/configure/account-choice?firstBuy=true`;
        if (type === 'individual') {
          navigate(relativeUrl);
        } else {
          navigateTo(`${shop}/${locale}/${orgId}${relativeUrl}`);
        }
      } catch (err) {
        console.error('Error updating cart:', err);
      }
    },
    [
      chooseProductOwner,
      dispatch,
      handleContinueClick,
      locale,
      navigate,
      params.cartId,
      shop,
      type,
    ],
  );
}
